import * as React from 'react';
import { Motion, spring } from 'react-motion';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { Spinner } from 'components/Elements/Spinner';

const variants = {
  primary: 'bg-[#01BAFF] text-white hover:bg-[#07232D]',
  light: 'bg-blue-100 dark:bg-gray-800 dark:text-white text-blue-800 hover:bg-blue-200',
  outlined: 'border hover:text-gray-900 hover:font-bold hover:text-gray-700 border-[#F3F4F6] text-[#6B7280]',
  dark: 'bg-black text-white hover:bg-gray-800',
  secondary:
    'bg-white dark:bg-gray-800 dark:text-gray-100 border dark:border-none dark:hover:bg-gray-900 dark:hover:text-[#166CAD] border-gradient-to-r from-[#166CAD] to-[#092C47] text-[#166CAD] hover:bg-gray-100',
  tertiary: 'bg-none text-[#166CAD] underline hover:font-semibold',
  success: 'bg-green-500 text-white hover:bg-green-600',
  danger: 'bg-red-500 text-white hover:bg-red-600',
  warning: 'bg-yellow-500 text-white hover:bg-yellow-600',
};

const sizes = {
  sm: 'py-2 px-4 text-sm',
  md: 'py-2 px-6 text-md',
  lg: 'py-4 px-8 text-lg',
  xl: 'py-5 px-10 text-xl',
};

export const Button = React.forwardRef(
  (
    {
      type = 'button',
      className = '',
      variant = 'primary',
      size = 'md',
      isLoading = false,
      startIcon,
      endIcon,
      ...props
    },
    ref
  ) => {
    const [isHovered, setIsHovered] = React.useState(false);
    const [isClicked, setIsClicked] = React.useState(false);

    return (
      <Motion
        style={{
          // When hovered, scale up slightly. 
          scale: spring(isHovered ? 1.05 : 1, { stiffness: 170, damping: 20 }),
          // When clicked, scale down slightly.
          pressScale: spring(isClicked ? 0.95 : 1, { stiffness: 200, damping: 15 }),
        }}
      >
        {({ scale, pressScale }) => {
          const finalScale = scale * pressScale;
          return (
            <button
              ref={ref}
              type={type}
              // Combine motion-based scale with Tailwind classes
              style={{ transform: `scale(${finalScale})` }}
              className={clsx(
                `flex justify-center items-center rounded-xl font-medium focus:outline-none
                 transition-all duration-300 px-2`,
                sizes[size],
                variants[variant],
                className,
                { 'opacity-70 cursor-not-allowed': props.disabled }
              )}
              // Handle mouse/touch events to drive the animation
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
              onMouseDown={() => setIsClicked(true)}
              onMouseUp={() => setIsClicked(false)}
              onTouchStart={() => setIsClicked(true)}
              onTouchEnd={() => setIsClicked(false)}
              {...props}
            >
              {isLoading && <Spinner size="sm" className="text-current" />}
              {!isLoading && startIcon}
              <span className="mx-2">{props.children}</span>
              {!isLoading && endIcon}
            </button>
          );
        }}
      </Motion>
    );
  }
);

Button.displayName = 'Button';

Button.propTypes = {
  type: PropTypes.string,
  className: PropTypes.string,
  variant: PropTypes.oneOf([
    'light',
    'outlined',
    'dark',
    'primary',
    'secondary',
    'success',
    'danger',
    'warning',
    'tertiary',
  ]),
  size: PropTypes.oneOf(['sm', 'md', 'lg', 'xl']),
  isLoading: PropTypes.bool,
  startIcon: PropTypes.node,
  endIcon: PropTypes.node,
};
